import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { TrendingUp, Receipt, Clock, RotateCcw, Tag } from "lucide-react";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { cn } from "../../lib/utils";
import { PtsExpenseSummaryService } from "@/services/APIs";
import {
  formatInr,
  formatMoney,
  formatDateDisplay,
  formatQuantity,
  isInrCurrency,
  toInr,
  avatarClass,
  initialOf,
} from "./utils";
import { USD_TO_INR_RATE } from "./constants";
import {
  ExpenseCategoryBadge,
  ExpenseFrequencyBadge,
  ExpenseStatusBadge,
} from "./ExpenseBadges";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
];

const getCategoryColor = (category: string, fallbackColor: string): string => {
  const normalized = String(category || "")
    .trim()
    .toLowerCase();
  switch (normalized) {
    case "ai tools":
      return "#6366f1"; // Indigo
    case "cloud":
      return "#0ea5e9"; // Sky
    case "communication":
      return "#10b981"; // Emerald
    case "domain & hosting":
    case "domain and hosting":
      return "#f59e0b"; // Amber
    case "software":
      return "#ef4444"; // Rose
    case "infrastructure":
      return "#8b5cf6"; // Violet
    case "other":
      return "#6b7280"; // Gray
    default:
      return fallbackColor;
  }
};

const ExpenseDashboardPage = () => {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [monthIndex, setMonthIndex] = useState(() => new Date().getMonth());

  const monthName = useMemo(() => {
    return new Date(2000, monthIndex, 1).toLocaleString("en-US", {
      month: "long",
    });
  }, [monthIndex]);

  const monthValue = useMemo(
    () => dayjs(new Date(year, monthIndex, 1)),
    [year, monthIndex]
  );

  // Fetch exchange rate from Frankfurter API
  const { data: exchangeRateData } = useQuery({
    queryKey: ["usd-to-inr-rate"],
    queryFn: async () => {
      try {
        const res = await fetch(
          "https://api.frankfurter.dev/v1/latest?base=USD&symbols=INR"
        );
        if (!res.ok) {
          throw new Error("Failed to fetch exchange rate");
        }
        const json = await res.json();
        const rate = json.rates.INR as number;
        console.log("Dashboard: Fetched USD to INR rate successfully:", rate);
        return rate;
      } catch (error) {
        console.error(
          "Dashboard: Error fetching exchange rate from API, using fallback:",
          error
        );
        throw error;
      }
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const exchangeRate = exchangeRateData ?? USD_TO_INR_RATE;

  useEffect(() => {
    console.log(
      "Dashboard: Current USD to INR exchange rate used:",
      exchangeRate
    );
  }, [exchangeRate]);

  // Fetch dashboard data
  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ["expense-dashboard", year, monthName],
    queryFn: () =>
      PtsExpenseSummaryService.getDashboard({
        month: monthName,
        year,
      }),
  });

  const handleMonthChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }
    setYear(value.year());
    setMonthIndex(value.month());
  };

  const handleReset = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonthIndex(now.getMonth());
  };

  // Process data for charts
  const barData = useMemo(() => {
    if (!dashboardData?.bar_chart) {
      return [];
    }
    return dashboardData.bar_chart.map((item: any) => {
      const pieMatch = dashboardData.pie_chart?.find(
        (p: any) =>
          String(p.category).toLowerCase() ===
          String(item.category).toLowerCase()
      );
      return {
        category: item.category,
        amount: Number(item.total_expense),
        count: pieMatch ? pieMatch.record_count : 0,
      };
    });
  }, [dashboardData]);

  const pieData = useMemo(() => {
    if (!dashboardData?.pie_chart) {
      return [];
    }
    return dashboardData.pie_chart.map((item: any) => ({
      name: item.category,
      value: Number(item.total_expense),
      count: item.record_count,
    }));
  }, [dashboardData]);

  const recentRecords = dashboardData?.recent_records ?? [];

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Expense Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Visual analytics and summary of company expenses
          </p>
        </div>
      </div>

      {/* Date Filter Card */}
      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="flex flex-col justify-between gap-4 p-4 sm:flex-row sm:items-end">
          <div className="flex min-w-[240px] flex-col gap-1.5">
            <Label
              htmlFor="dashboard-month"
              className="text-[11px] font-medium uppercase tracking-wide text-gray-500"
            >
              Select Month
            </Label>
            <DatePicker
              id="dashboard-month"
              picker="month"
              allowClear={false}
              value={monthValue}
              onChange={handleMonthChange}
              format="MMMM, YYYY"
              className="h-9 w-full rounded-lg border-gray-200 bg-[#fafafa]"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 w-[110px] shrink-0"
            onClick={handleReset}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" />
            Reset
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="py-20 text-center text-gray-500">
          Loading Dashboard...
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="border-gray-200 bg-white shadow-sm transition-transform hover:scale-[1.01]">
              <CardContent className="flex items-center justify-between p-5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">
                    Total Expense
                  </p>
                  <p className="text-2xl font-bold text-[#111827]">
                    {formatInr(Number(dashboardData?.total_expense ?? 0))}
                  </p>
                  <p className="text-xs text-gray-400">
                    For {monthName} {year}
                  </p>
                </div>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  <Receipt className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white shadow-sm transition-transform hover:scale-[1.01]">
              <CardContent className="flex items-center justify-between p-5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">
                    One-Time Expenses
                  </p>
                  <p className="text-2xl font-bold text-[#111827]">
                    {formatInr(
                      Number(
                        dashboardData?.frequency?.one_time?.total_expense ?? 0
                      )
                    )}
                  </p>
                  <p className="text-xs font-medium text-orange-600">
                    Count: {dashboardData?.frequency?.one_time?.count ?? 0}
                  </p>
                </div>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                  <Tag className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white shadow-sm transition-transform hover:scale-[1.01]">
              <CardContent className="flex items-center justify-between p-5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">
                    Monthly Expenses
                  </p>
                  <p className="text-2xl font-bold text-[#111827]">
                    {formatInr(
                      Number(
                        dashboardData?.frequency?.monthly?.total_expense ?? 0
                      )
                    )}
                  </p>
                  <p className="text-xs font-medium text-emerald-600">
                    Count: {dashboardData?.frequency?.monthly?.count ?? 0}
                  </p>
                </div>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <Clock className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white shadow-sm transition-transform hover:scale-[1.01]">
              <CardContent className="flex items-center justify-between p-5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">
                    Yearly Expenses
                  </p>
                  <p className="text-2xl font-bold text-[#111827]">
                    {formatInr(
                      Number(
                        dashboardData?.frequency?.yearly?.total_expense ?? 0
                      )
                    )}
                  </p>
                  <p className="text-xs font-medium text-sky-600">
                    Count: {dashboardData?.frequency?.yearly?.count ?? 0}
                  </p>
                </div>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                  <TrendingUp className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Bar Chart Card */}
            <Card className="border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Expenses by Category
                </h3>
                <p className="text-sm text-gray-500">
                  Total expense value comparison
                </p>
              </div>
              <div className="w-full min-w-0" style={{ height: 300 }}>
                {barData.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-gray-400">
                    No chart data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barData}
                      margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis
                        dataKey="category"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        axisLine={{ stroke: "#e5e7eb" }}
                      />
                      <YAxis
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        axisLine={{ stroke: "#e5e7eb" }}
                        tickFormatter={val => `₹${val / 1000}k`}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload?.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                                <p className="font-semibold text-gray-900">
                                  {data.category}
                                </p>
                                <p className="mt-1 text-sm text-gray-600">
                                  Total Expense:{" "}
                                  <span className="font-medium text-gray-900">
                                    {formatInr(data.amount)}
                                  </span>
                                </p>
                                <p className="text-sm text-gray-600">
                                  Record Count:{" "}
                                  <span className="font-medium text-gray-900">
                                    {data.count}
                                  </span>
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                        {barData.map((entry: any, index: number) => (
                          <Cell
                            key={`cell-${entry.category}`}
                            fill={getCategoryColor(
                              entry.category,
                              COLORS[index % COLORS.length]
                            )}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>

            {/* Pie Chart Card */}
            <Card className="border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Expense Distribution
                </h3>
                <p className="text-sm text-gray-500">
                  Visual share of total costs
                </p>
              </div>
              <div className="w-full min-w-0" style={{ height: 300 }}>
                {pieData.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-gray-400">
                    No chart data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {pieData.map((entry: any, index: number) => (
                          <Cell
                            key={`cell-${entry.name}`}
                            fill={getCategoryColor(
                              entry.name,
                              COLORS[index % COLORS.length]
                            )}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={value => [
                          formatInr(Number(value)),
                          "Expense",
                        ]}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        iconType="circle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </div>

          {/* Recent Records Table */}
          <Card className="border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold text-[#111827]">
                  Recent Expenses
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  Latest expense transactions for {monthName}
                </p>
              </div>
              <Link
                to={PTSNavigationRoutes.EXPENSE_SUMMARY}
                className="text-sm font-semibold text-[#6366f1] transition-colors hover:text-[#4f46e5] hover:underline"
              >
                View All
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-5 py-3 font-medium">Expense</th>
                    <th className="px-5 py-3 font-medium">Category</th>
                    <th className="px-5 py-3 font-medium">Vendor</th>
                    <th className="px-5 py-3 font-medium">Frequency</th>
                    <th className="px-5 py-3 font-medium">Quantity</th>
                    <th className="px-5 py-3 font-medium">Unit Cost</th>
                    <th className="px-5 py-3 font-medium">Total Expense</th>
                    <th className="px-5 py-3 font-medium">Total Expense(₹)</th>
                    <th className="px-5 py-3 font-medium">Billing Date</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        No transactions logged for this month.
                      </td>
                    </tr>
                  ) : (
                    recentRecords.map((item: any) => (
                      <tr
                        key={item.id}
                        className="border-b border-gray-50 last:border-0"
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <span
                              className={cn(
                                "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold",
                                avatarClass(item.title)
                              )}
                            >
                              {initialOf(item.title)}
                            </span>
                            <span className="font-medium text-[#111827]">
                              {item.title ?? "—"}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <ExpenseCategoryBadge category={item.category} />
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {item.vendor ?? "—"}
                        </td>
                        <td className="px-5 py-3.5">
                          <ExpenseFrequencyBadge frequency={item.frequency} />
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatQuantity(item.quantity)}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatMoney(item.unit_cost, item.currency)}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="font-semibold text-[#111827]">
                            {formatMoney(item.total_expense, item.currency)}
                          </div>
                          {!isInrCurrency(item.currency) && (
                            <div className="text-xs text-gray-400">
                              {formatInr(
                                toInr(
                                  item.total_expense,
                                  item.currency,
                                  exchangeRate
                                )
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="font-semibold text-[#111827]">
                            {formatInr(
                              toInr(
                                item.total_expense,
                                item.currency,
                                exchangeRate
                              )
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatDateDisplay(item.billing_date)}
                        </td>
                        <td className="px-5 py-3.5">
                          <ExpenseStatusBadge status={item.status} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default ExpenseDashboardPage;
